import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import notificationService from '../services/notifications';

interface User {
  id: number;
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if user is already logged in
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');
        
        console.log('Auth initialization - Token:', !!token, 'User:', !!savedUser);
        
        if (token && savedUser) {
          try {
            const userData = JSON.parse(savedUser);
            setUser(userData);
            setIsAuthenticated(true);
            
            console.log('Attempting token validation...');
            // Validate token with backend
            const response = await authAPI.validateToken();
            console.log('Token validation successful');
            
            // Token is valid, update user data if needed
            setUser(response.data.user);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            setIsAuthenticated(true);

            // Initialize and subscribe to push notifications for already-logged-in users
            if (notificationService.isSupported()) {
              try {
                const initialized = await notificationService.initialize();
                if (initialized) {
                  await notificationService.subscribe();
                  console.log('Push notifications subscribed (existing session)');
                }
              } catch (error) {
                console.error('Error setting up notifications on page load:', error);
                // Don't block auth if notification setup fails
              }
            }
          } catch (error) {
            console.log('Token validation failed:', error);
            // Token is invalid, clear it
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          console.log('No token or user found');
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.log('Auth initialization error:', error);
        // If user data is corrupted, clear it
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login(email, password);
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      setUser(user);
      setIsAuthenticated(true);

      // Initialize and subscribe to push notifications
      if (notificationService.isSupported()) {
        try {
          const initialized = await notificationService.initialize();
          if (initialized) {
            await notificationService.subscribe();
            console.log('Push notifications subscribed');
          }
        } catch (error) {
          console.error('Error setting up notifications:', error);
          // Don't block login if notification setup fails
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      const response = await authAPI.register(name, email, password);
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      setUser(user);
      setIsAuthenticated(true);

      // Initialize and subscribe to push notifications
      if (notificationService.isSupported()) {
        try {
          const initialized = await notificationService.initialize();
          if (initialized) {
            await notificationService.subscribe();
            console.log('Push notifications subscribed');
          }
        } catch (error) {
          console.error('Error setting up notifications:', error);
          // Don't block registration if notification setup fails
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    // Unsubscribe from notifications
    if (notificationService.isSupported()) {
      try {
        await notificationService.unsubscribe();
      } catch (error) {
        console.error('Error unsubscribing from notifications:', error);
      }
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
